// src/features/orders/clientSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Client type
export interface Client {
  id: string;
  [key: string]: any; // flexible, allow extra fields from API
}

// API response shape for list
interface ClientListResponse {
  clients: Client[];
  total: number;
  page: number;
  totalPages: number;
}

// Slice state
interface ClientState {
  data: ClientListResponse | null;
  loading: boolean;
  error: string | null;
}

// Payload for updating a client
export interface ClientUpdatePayload {
  id: string;
  [key: string]: any;
}

// ✅ Initial state
const initialState: ClientState = {
  data: null,
  loading: false,
  error: null,
};

// ✅ Slice
const clientSlice = createSlice({
  name: 'client',
  initialState,
  reducers: {
    // Set full client list response
    setClientList(state, action: PayloadAction<ClientListResponse>) {
      state.data = action.payload;
      state.loading = false;
    },

    // Add a new client
    addClient(state, action: PayloadAction<any>) {
      const details = action.payload;

      if (!details) return;

      const newClient: Client = {
        ...details
      };

      if (state.data) {
        state?.data?.clients?.unshift(newClient);
        state.data.total += 1;

        // Keep only 10 in list
        if (state.data.clients.length > 10) {
          state?.data?.clients?.pop();
        }
      } else {
        state.data = {
          clients: [newClient],
          total: 1,
          page: 1,
          totalPages: 1,
        };
      }
    },


    // Update client by id
    clientUpdate(state, action: PayloadAction<ClientUpdatePayload>) {
      const { id, ...updates } = action.payload;
      if (!id || !state.data?.clients) return;

      const client = state.data.clients.find((p) => p.id === id);
      if (client) {
        Object.assign(client, updates);
      }
    },

    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },

    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
  },
});

// ✅ Export actions
export const {
  setClientList,
  addClient,
  clientUpdate,
  setLoading,
  setError,
} = clientSlice.actions;

// ✅ Export reducer
export default clientSlice.reducer;
